import { type } from "arktype";
export const subMonthIntervalSchema = type("'WEEKLY' | 'EVERY_2_WEEKS' | 'TWICE_A_MONTH' | 'MONTHLY'");
/**
 * ⚠ **Adding a value takes two releases.** Budget config is shared across a household's devices, so a
 * value one phone writes is read by phones still on an older bundle — where an unknown interval THROWS
 * in `convertToAproximateMonthly`, and is either scheduled MONTHLY (bundles before `EVERY_2_MONTHS`) or
 * treated as "no due date" (bundles since) by the bill schedule. Ship every reader first, and only then
 * offer the value in the bill picker (`BILL_INTERVALS` in the mobile bill editor). "Every reader" includes
 * every live NATIVE binary, which only gets new code by OTA if its runtime still receives them. The
 * concrete gate is in `libraries/roo-shared/CLAUDE.md` § "JSONB shape changes are release-relevant".
 */
export const extendedIntervalSchema = type("'WEEKLY' | 'EVERY_2_WEEKS' | 'TWICE_A_MONTH' | 'MONTHLY' | 'EVERY_2_MONTHS' | 'QUARTERLY' | 'EVERY_6_MONTHS' | 'YEARLY'");
export const rooSymbolSimpleSchema = type({
    type: "'simple'",
    color: "string",
});
export const rooSymbolEmojiSchema = type({
    type: "'emoji'",
    /** An emoji character (which should be rendered as a plain string; this is not a key into a map of allowed values or anything) */
    character: "string",
});
// | RooSymbolEmote;
// /** An animated image */
// interface RooSymbolEmote {
//   type: "emote";
//   name: "roo_dancing" | "roo_jumping";
// }
export const rooSymbolSchema = rooSymbolSimpleSchema.or(rooSymbolEmojiSchema);
/**
 * The Android notification channel every Roo push is presented on. A 2-copy contract, which is why it
 * lives here rather than in either app: mobile CREATES the channel under this id at startup
 * (`pushNotification.service.tsx` `init`), and the server STAMPS it onto the outbound FCM payload
 * (`pushNotification.service.ts`). If the two ever disagree, expo silently falls back to its own
 * `expo_notifications_fallback_notification_channel` — a channel the user sees as "Miscellaneous" and
 * can block independently of the app-level permission (T-305 Defect 5).
 *
 * The id is opaque and PERMANENT — Android keys the user's per-channel settings (importance, sound,
 * blocked-or-not) on it, and a changed id reads as a brand-new channel, resetting those settings and
 * leaving the old one orphaned in system settings. Change `ANDROID_PUSH_CHANNEL_NAME` (the display
 * label, which Android does let you update in place) instead.
 */
export const ANDROID_PUSH_CHANNEL_ID = "default";
/** The user-visible label for `ANDROID_PUSH_CHANNEL_ID` in Android's per-app notification settings. */
export const ANDROID_PUSH_CHANNEL_NAME = "Roo Money";
/**
 * Name of the web app's refresh-token cookie.
 *
 * A server↔browser wire contract, so it lives here rather than in either side — same reasoning as
 * `ANDROID_PUSH_CHANNEL_ID` above. The server issues and reads it; the web app's tests assert it is
 * NOT reachable from JS. Re-typing the literal in a spec would let a rename leave that suite green
 * while asserting a name nothing uses.
 *
 * Deliberately opaque — it names no product concept.
 */
export const WEB_REFRESH_COOKIE_NAME = "roo_rt";
/**
 * How long a WEB refresh token lives. Mobile's stays at 180 days (`generateRefreshToken`'s default).
 *
 * A phone app you might not open for months needs a long token; a desktop companion does not, and a
 * shorter life bounds how long a token lifted from a browser profile stays usable. The server slides
 * it forward on use, so an active session is never cut off at the wall.
 */
export const WEB_REFRESH_TOKEN_DAYS = 28;
export var ChartPeriod;
(function (ChartPeriod) {
    ChartPeriod["1w"] = "1w";
    ChartPeriod["1m"] = "1m";
    ChartPeriod["1y"] = "1y";
    ChartPeriod["5y"] = "5y";
})(ChartPeriod || (ChartPeriod = {}));
/**
 * Note: ES2015+ guarantees order, so these are given in the order in which they should appear.
 * TODO: Consider moving some of these into new EntryOrTxnListTypeFilterNames:
 *       FINAL_TXN_DELETE, PENDING_GHOST, FINAL_ACCOUNT_ADDTXNS, FINAL_ACCOUNT_FREETXNS
 */
export const ENTRY_OR_TXN_TYPE_FILTER_MAP = {
    assigned: ["FINAL_TXN_ASSIGNED_NORMAL", "FINAL_TXN_PAYBACK_ASSIGNED", "PENDING_GHOST"],
    income: ["FINAL_TXN_ASSIGNED_INCOME", "FINAL_TXN_PAYBACK_INCOME", "FINAL_MANUAL_INCOME"],
    dailyCash: ["FINAL_DAILY_CASH"],
    matches: ["PENDING_TXN_MATCH", "FINAL_TXN_MATCH", "FINAL_TXN_PAYBACK_MATCH"],
    // T-233: split out of `matches`. A payback payment writes a LineItem on the PAYBACK pouch, so it
    // moves that pouch's balance — but `matches` is account-context-only, so in a pouch view these rows
    // were excluded by default AND had no toggle to switch on, leaving the Payback pouch's history
    // unable to account for its own balance. This group is relevant to BOTH contexts.
    paybackPayment: ["FINAL_TXN_HALFPAYBACK_PAYMENT"],
    // The borrow/cash-advance mirror, kept as its OWN group rather than folded into `assigned`. Its
    // ON_BUDGET half carries ordinary pouch LineItems (deliberately NOT a PAYBACK-pouch one), so those
    // rows are part of what explains each destination pouch's balance and must be reachable in a pouch
    // view — the same reasoning that split `paybackPayment` out of `matches` in T-233. Its own name
    // also keeps "borrowed money arrived here" distinguishable from ordinary spending in the filter UI.
    paybackAdvance: ["FINAL_TXN_HALFPAYBACK_ASSIGNED"],
    ignored: ["FINAL_TXN_IGNORE", "FINAL_TXN_PAYBACK_IGNORE"],
    unprocessed: ["PENDING_TXN_NEW", "PENDING_TXN_AWAITPOST"],
    account: [
        "FINAL_ACCOUNT_ARCHIVE",
        "FINAL_ACCOUNT_INIT",
        "FINAL_ACCOUNT_TO_PAYBACK",
        "FINAL_ACCOUNT_ADDTXNS",
        "FINAL_ACCOUNT_FREETXNS",
    ],
    bounce: ["FINAL_BOUNCE"],
    // `null` = an entryless off-budget txn; FINAL_TXN_OFF_BUDGET = a txn taken off the budget (T-103),
    // which still has a (terminal) entry. Both are "off budget" for list-filter purposes.
    offBudget: [null, "FINAL_TXN_OFF_BUDGET"],
    manualAdjustment: ["FINAL_ACCOUNT_ADJUSTMENT"],
};
const UNSEARCHABLE_ENTRY_TYPES = ["PENDING_DAILY_CASH", "FINAL_TXN_DELETE"];
const _typecheck_ENTRY_OR_TXN_TYPE_FILTER_MAP_exhaustive = true;
export const snoozeableRepeatableAckSchema = type({
    /** Update this to the current date once a user acks without snoozing. */
    lastAckedMS: "number",
    /**
     * Update this instead of lastAckedMS to defer the reminder/notification without acking it.
     * Should deleted upon updating lastAckedMS, and oughtn't be set if <= lastAckedMS or > now.
     */
    "snoozeUntilMS?": "number",
});
/**
 * Gives the dates of most recent acknowledgement for various reminders and notifications, so that
 *   a user can dismiss those reminders and have them not appear again for anyone on the budget
 *   (until the next time the notification becomes relevant).
 * In particular, this gives only the sort that are on a per-budget (not per-user) basis.
 * Items herein should be created lazily as needed (typically after first acknowledgment), and
 *   deleted once irrelevant (though the code should not error out if there is extra stuff here).
 */
export const budgetNotificationAcksSchema = type({
    /**
     * For reminders that occur when PaybackPlan.accountsPrioritized.reminderDay is set.
     * Should include ALL accounts for which a reminderDay is set (and no others), and should
     *   initialize to one-day-before-reminderDay-got-set (so a card appears instantly only if
     *   reminderDay was "today"; see the getNextReminderCardDate function).
     * Note that these do not mean a payment has been made; Roo can't know that easily.  It just
     *   means the user dismissed the reminder.  (It's possible they paid before even seeing the
     *   reminder).
     * Keys are Account ids.
     */
    "paybackPaymentReminders?": type({
        "[string]": snoozeableRepeatableAckSchema,
    }),
});
//# sourceMappingURL=misc.model.js.map